/**
 * Provider Logo DTOs
 * Client-safe types for logo upload, processing, and management
 */

// ==================== Crop Data ====================

export interface CropData {
  /** Horizontal position as percentage (0-100) */
  x: number;
  /** Vertical position as percentage (0-100) */
  y: number;
  /** Width as percentage (0-100) */
  width: number;
  /** Height as percentage (0-100) */
  height: number;
  /** Zoom level (1.0 - 3.0) */
  zoom: number;
  /** 
   * When true, places the logo on a white square background instead of cropping.
   * Allows rectangle logos to be positioned freely (can overflow boundaries).
   * The x/y values represent the logo's position offset on the canvas.
   */
  useWhiteBackground?: boolean;
}

// ==================== Upload URL ====================

export interface UploadUrlRequestDto {
  /** Original filename with extension */
  filename: string;
  /** MIME type of the file */
  contentType: string;
  /** File size in bytes */
  fileSizeBytes: number;
}

export interface UploadUrlResponseDto {
  /** Pre-signed S3 URL for PUT upload */
  uploadUrl: string;
  /** Unique identifier for this upload */
  fileId: string;
  /** URL expiration timestamp */
  expiresAt: Date;
}

// ==================== Process Logo ====================

export interface ProcessLogoRequestDto {
  /** File ID returned from upload-url endpoint */
  fileId: string;
  /** Crop coordinates (required for raster images, null for SVG) */
  cropData?: CropData | null;
}

export interface ProcessLogoResponseDto {
  /** CDN URL for the processed logo (512px version) */
  cdnUrl: string;
  /** Logo metadata */
  logo: ProviderLogoResponseDto;
}

// ==================== Recrop Logo ====================

export interface RecropLogoRequestDto {
  /** New crop coordinates */
  cropData: CropData;
}

// ==================== Provider Logo ====================

export interface ProviderLogoResponseDto {
  id: string;
  fileId: string;
  originalFilename: string;
  mimeType: string;
  fileSizeBytes: number;
  isSvg: boolean;
  cropData: CropData | null;
  cdnUrl: string;
  createdAt: Date;
  updatedAt: Date;
}

// ==================== Logo Metadata (for edit) ====================

export interface LogoMetadataResponseDto {
  /** Logo metadata */
  logo: ProviderLogoResponseDto;
  /** Pre-signed URL to original image (1 hour expiry) for edit UI */
  originalUrl: string;
}

// ==================== Allowed MIME Types ====================

export const ALLOWED_LOGO_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/svg+xml',
] as const;

export type AllowedLogoMimeType = (typeof ALLOWED_LOGO_MIME_TYPES)[number];

// ==================== Validation Constants ====================

export const LOGO_UPLOAD_CONSTRAINTS = {
  /** Maximum file size in bytes (6MB) */
  MAX_FILE_SIZE_BYTES: 6 * 1024 * 1024,
  /** Minimum image dimension in pixels */
  MIN_IMAGE_DIMENSION_PX: 128,
  /** Maximum zoom level */
  MAX_ZOOM: 3.0,
  /** Minimum zoom level (white-background mode supports zooming out) */
  MIN_ZOOM: 0.2,
  /** Upload URL expiry in seconds (15 minutes) */
  UPLOAD_URL_EXPIRY_SECONDS: 900,
  /** Read URL expiry in seconds (1 hour) */
  READ_URL_EXPIRY_SECONDS: 3600,
} as const;
